import React, { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import Modal from '@components/Modal';
import XCircle from '@components/Icons/XCircle';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useToast, useUser } from '@/hooks';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';

import ManualUpload, { type ModalConfig } from './ManualUpload';
import ChooseType from './ChooseType';
import s from './UploadBankStatementModal.module.scss';

type Props = {
  bankAccountIntegrationId: string;
  creatorId: string;
  onClose: () => void;
  onBankAccountAdded: () => void;
  config?: ModalConfig;
};

const UploadBankStatementsModal: React.FC<Props> = ({
  bankAccountIntegrationId,
  creatorId,
  onBankAccountAdded,
  onClose,
  config,
}) => {
  const { getData: getCreateLinkToken, data: plaidToken } = useNoodleApi(tsClient.plaid.createLinkToken);
  const { getData: getBankAccountDocumentsFromPlaid } = useNoodleApi(tsClient.plaid.getBankAccountDocumentsFromPlaid, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'Failed to exchange public token.',
    ],
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Bank statements added successfully'],
  });
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [user] = useUser();
  const [step, setStep] = useState<'choose' | 'manual' | 'plaid'>(config?.step ?? 'choose');
  const [uploadType, setUploadType] = useState<'plaid' | 'manual'>(config?.step === 'manual' ? 'manual' : 'plaid');
  const [isModalBusy, setIsModalBusy] = useState<boolean>(false);

  const handleFinishUpload = async (): Promise<void> => {
    onBankAccountAdded();
    onClose();
  };

  useEffect(() => {
    if (user?.id) {
      getCreateLinkToken({ creatorId });
    }
  }, [getCreateLinkToken, creatorId, user?.id]);

  const { open, ready } = usePlaidLink({
    onExit: err => {
      if (err) {
        setError('Failed to connect with Plaid');
      }
      setIsModalBusy(false);
    },
    onSuccess: async (publicToken, metaData) => {
      if (!publicToken) {
        setError('No public token received');
        setIsModalBusy(false);
        return;
      }
      await getBankAccountDocumentsFromPlaid({
        bankAccountIntegrationId,
        creatorId,
        metaData,
        publicToken,
      });
      onBankAccountAdded();
      onClose();
      setIsModalBusy(false);
    },
    token: plaidToken?.linkToken || null,
  });

  const handleNext = (): void => {
    if (uploadType === 'plaid') {
      if (ready) {
        setIsModalBusy(true);
        open();
      }
    } else {
      setStep('manual');
    }
  };

  const handleClose = (): void => {
    if (step === 'manual' && config?.step !== 'manual') {
      setStep('choose');
    } else {
      onClose();
    }
  };

  return (
    <>
      {!isModalBusy && <Modal
        hideBottomInset
        title="Upload Bank Statements"
        closeIcon={
          step === 'manual' ? <ArrowCircleLeft size={24} weight="fill" /> : <XCircle weight="fill" size={24} color="var(--color-gray-100)" />
        }
        onClose={handleClose}
      >
        {(error || uploadError) && <div className={s.error}><BannerStatus status='error' label="Failed to add account" /></div>}
        <>
          {step === 'choose' && (
            <ChooseType
              uploadType={uploadType}
              onSelectType={setUploadType}
              onClickNext={handleNext}
            />
          )}
          {step === 'manual' && (
            <ManualUpload
              bankAccountIntegrationId={bankAccountIntegrationId}
              creatorId={creatorId}
              modalConfig={config}
              onError={setUploadError}
              onFinish={handleFinishUpload}
            />
          )}
        </>
      </Modal>}
    </>
  );
};

export default UploadBankStatementsModal;
export type {
  ModalConfig,
};
